import { useState, useMemo, useCallback, useEffect } from 'react';
import { Combobox } from '@headlessui/react';
import { Check, ChevronsUpDown, MapPin, Loader2 } from 'lucide-react';
import { REGIONS } from '../../data/mockData';
import { useI18n } from '../../i18n';

// Cyrillic to Latin transliteration map for Serbian
const cyrillicToLatinMap: { [key: string]: string } = {
  'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'ђ': 'đ', 'е': 'e', 'ж': 'ž',
  'з': 'z', 'и': 'i', 'ј': 'j', 'к': 'k', 'л': 'l', 'љ': 'lj', 'м': 'm', 'н': 'n',
  'њ': 'nj', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'ћ': 'ć', 'у': 'u',
  'ф': 'f', 'х': 'h', 'ц': 'c', 'ч': 'č', 'џ': 'dž', 'ш': 'š',
  'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D', 'Ђ': 'Đ', 'Е': 'E', 'Ж': 'Ž',
  'З': 'Z', 'И': 'I', 'Ј': 'J', 'К': 'K', 'Л': 'L', 'Љ': 'Lj', 'М': 'M', 'Н': 'N',
  'Њ': 'Nj', 'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T', 'Ћ': 'Ć', 'У': 'U',
  'Ф': 'F', 'Х': 'H', 'Ц': 'C', 'Ч': 'Č', 'Џ': 'Dž', 'Ш': 'Š'
};

function cyrillicToLatin(text: string): string {
  return text.split('').map(char => cyrillicToLatinMap[char] || char).join('');
}

// Address details from OSM
export interface OsmAddressDetails {
  address: string;
  city: string;
  municipality: string;
  region: string;
  lat?: number;
  lng?: number;
}

interface LocationSearchProps {
  value: string;
  onChange: (value: string, lat?: number, lng?: number, addressDetails?: OsmAddressDetails) => void;
  className?: string;
  placeholder?: string;
  showOsmSearch?: boolean;
}

interface LocationOption {
  id: string;
  name: string;
  type: 'region' | 'subregion' | 'osm';
  parent?: string;
  lat?: number;
  lng?: number;
  displayName?: string;
  addressDetails?: OsmAddressDetails;
}

export function LocationSearch({ value, onChange, className, placeholder, showOsmSearch = true }: LocationSearchProps) {
  const [query, setQuery] = useState('');
  const [osmResults, setOsmResults] = useState<LocationOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const { t, language } = useI18n();

  // Lokalne lokacije iz mock podataka
  const localLocations = useMemo(() => {
    const list: LocationOption[] = [];
    REGIONS.forEach(region => {
      // Add region with its coordinates
      list.push({ 
        id: `r-${region.id}`, 
        name: region.name, 
        type: 'region',
        lat: region.lat,
        lng: region.lng
      });
      region.cities.forEach(city => {
        list.push({
          id: `c-${city.id}`,
          name: city.name,
          type: 'subregion',
          parent: region.name,
          lat: city.lat,
          lng: city.lng
        });
      });
    });
    return list;
  }, []);

  // OSM Nominatim pretraga
  const searchOsm = useCallback(async (searchQuery: string) => {
    if (!showOsmSearch || searchQuery.length < 3) {
      setOsmResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Pretraga u Srbiji - request Latin script by using accept-language: en
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&countrycodes=rs&limit=5&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'en,sr-Latn', // Request Latin script
            'User-Agent': 'SvojNaSvom/1.0 (https://svojnasvom.com; contact@svojnasvom.com)'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const results: LocationOption[] = data.map((item: any, index: number) => {
          const addr = item.address || {};
          
          // Extract city/municipality from OSM address
          const city = cyrillicToLatin(
            addr.city || addr.town || addr.village || addr.hamlet || addr.suburb || ''
          );
          const municipality = cyrillicToLatin(
            addr.municipality || addr.county || addr.city_district || ''
          );
          const region = cyrillicToLatin(
            addr.state || addr.region || addr.state_district || ''
          );
          
          // Build display name in Latin
          const displayNameLatin = cyrillicToLatin(item.display_name);
          const nameLatin = cyrillicToLatin(item.display_name.split(',')[0]);
          
          return {
            id: `osm-${item.place_id || index}`,
            name: nameLatin,
            displayName: displayNameLatin,
            type: 'osm' as const,
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lon),
            addressDetails: {
              address: displayNameLatin,
              city: city,
              municipality: municipality,
              region: region,
              lat: parseFloat(item.lat),
              lng: parseFloat(item.lon),
            }
          };
        });
        setOsmResults(results);
      }
    } catch (error) {
      console.error('OSM search error:', error);
      setOsmResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [showOsmSearch]);

  // Debounced OSM pretraga
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length >= 3) {
        searchOsm(query);
      } else {
        setOsmResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, searchOsm]);

  // Filtrirane lokalne lokacije
  const filteredLocalLocations = query === ''
    ? localLocations
    : localLocations.filter((location) =>
        location.name.toLowerCase().includes(query.toLowerCase())
      );

  // Kombinovani rezultati
  const allResults = useMemo(() => {
    const results: LocationOption[] = [];

    // Prvo dodaj lokalne rezultate
    if (filteredLocalLocations.length > 0) {
      results.push(...filteredLocalLocations.slice(0, 10));
    }

    // Zatim dodaj OSM rezultate ako ima pretrage
    if (osmResults.length > 0) {
      results.push(...osmResults);
    }

    return results;
  }, [filteredLocalLocations, osmResults]);

  const handleSelect = (selectedName: string) => {
    // Pronađi izabranu lokaciju
    const selectedLocation = allResults.find(loc => {
      if (loc.type === 'osm') {
        return loc.displayName === selectedName || loc.name === selectedName;
      }
      return loc.name === selectedName;
    });

    if (selectedLocation) {
      onChange(selectedName, selectedLocation.lat, selectedLocation.lng, selectedLocation.addressDetails);
    } else {
      onChange(selectedName);
    }
  };

  return (
    <Combobox value={value} onChange={handleSelect}>
      <div className={`relative ${className}`}>
        <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white dark:bg-slate-700 text-left border border-slate-300 dark:border-slate-600 focus-within:ring-2 focus-within:ring-amber-500">
          <Combobox.Input
            className="w-full border-none py-3 pl-4 pr-10 text-sm leading-5 text-slate-900 dark:text-white bg-transparent focus:ring-0 outline-none placeholder-slate-400"
            displayValue={(val: string) => val}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder || (language === 'sr' ? 'Lokacija' : 'Location')}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
            {isSearching ? (
              <Loader2 className="h-4 w-4 text-slate-400 animate-spin" aria-hidden="true" />
            ) : (
              <ChevronsUpDown className="h-4 w-4 text-slate-400" aria-hidden="true" />
            )}
          </Combobox.Button>
        </div>
        <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-slate-800 py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-[100]">
          {allResults.length === 0 && query !== '' && !isSearching ? (
            <div className="relative cursor-default select-none px-4 py-2 text-slate-500 dark:text-slate-400">
              {language === 'sr' ? 'Nema rezultata. Pokušajte sa drugom pretragom.' : 'No results. Try a different search.'}
            </div>
          ) : (
            <>
              {/* Lokalni rezultati - regioni i podregioni */}
              {filteredLocalLocations.length > 0 && (
                <>
                  <div className="px-3 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700">
                    {language === 'sr' ? 'Regioni i podregioni' : 'Regions and subregions'}
                  </div>
                  {filteredLocalLocations.slice(0, 10).map((location) => (
                    <Combobox.Option
                      key={location.id}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                          active ? 'bg-amber-500 text-white' : 'text-slate-900 dark:text-white'
                        }`
                      }
                      value={location.name}
                    >
                      {({ selected, active }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {location.name}
                            {location.type === 'subregion' && (
                              <span className={`text-xs ml-1 ${active ? 'text-amber-100' : 'text-slate-500 dark:text-slate-400'}`}>
                                ({location.parent})
                              </span>
                            )}
                          </span>
                          {selected && (
                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-amber-600'}`}>
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                </>
              )}

              {/* OSM rezultati - ulice i adrese */}
              {osmResults.length > 0 && (
                <>
                  <div className="px-3 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700 mt-1">
                    {language === 'sr' ? 'Ulice i adrese (OpenStreetMap)' : 'Streets and addresses (OpenStreetMap)'}
                  </div>
                  {osmResults.map((location) => (
                    <Combobox.Option
                      key={location.id}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                          active ? 'bg-amber-500 text-white' : 'text-slate-900 dark:text-white'
                        }`
                      }
                      value={location.displayName || location.name}
                    >
                      {({ selected, active }) => (
                        <>
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                            <MapPin className={`h-4 w-4 ${active ? 'text-white' : 'text-slate-400'}`} aria-hidden="true" />
                          </span>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {location.displayName || location.name}
                          </span>
                          {selected && (
                            <span className={`absolute inset-y-0 right-0 flex items-center pr-3 ${active ? 'text-white' : 'text-amber-600'}`}>
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                </>
              )}

              {/* Loading indicator */}
              {isSearching && (
                <div className="relative cursor-default select-none px-4 py-2 text-slate-500 dark:text-slate-400 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {language === 'sr' ? 'Pretražujem...' : 'Searching...'}
                </div>
              )}
            </>
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  );
}
